import { Box, Link, Typography } from "@material-ui/core";
import { OpenInNew } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { getColumnClassName } from "../../../components/flexible/FieldWrapper";
import { Article71Indication, AttributeSize, User } from "../../../models/generated/graphql";
import startUpper from "../../../utils/start-upper";
import useStyles from "../use-styles";
import useFieldStyles from "../../data/forms/forms-styles";
import CaseAssignee from "./CaseAssignee";
import { IAttribute } from "./Overview";
import { PatientFieldWrapper } from "./PatientFieldsWrapper";
import { localeDate } from "../../../utils/locale-date";
import { EMPTY_VALUE_PLACEHOLDER } from "../../../models/common/data";

export interface MainCaseInformationProps {
	patientAttributes?: IAttribute[] | undefined;
}

export const MainCaseInformationDetails = (props: MainCaseInformationProps) => {
	return (
		<Box padding={1} display="flex" width="100%">
			{(props.patientAttributes || []).map((f, fi) => (
				<PatientFieldWrapper key={fi} attribute={f.attribute} value={f.value} />
			))}
		</Box>
	);
};
export interface ICaseInformationDetails extends MainCaseInformationProps {
	caseId?: string;
	agreementId?: string;
	creationDate?: string;
	legalEntity?: string;
	agreementType?: string;
	isFormOwner?: boolean;
	assignedTo?: User[] | null;
	relevantIndication?: Article71Indication;
	customCaseDetailsData?: IAttribute[] | undefined;
}

const getLengthShortAttributes = (attributeSize: AttributeSize): number => {
	switch (attributeSize) {
		case AttributeSize.Long:
			return 4;
		case AttributeSize.Medium:
			return 2;
		case AttributeSize.Short:
			return 1;
		default:
			return 1;
	}
};

const CaseInformationDetails = (props: ICaseInformationDetails) => {
	const { t } = useTranslation();
	const { icon, fw400 } = useStyles();
	const styles = useFieldStyles();
	const totalRowsInShortAttributeCount = 16;
	function handleNavigateToAgreement(e: React.SyntheticEvent, id: string) {
		e.preventDefault();
		const currentURL = new URL(window.location.href);
		window.open(`${currentURL.origin}/agreements/agreement?id=${id}`, "_blank");
	}
	const reducer: any =
		props.customCaseDetailsData?.length &&
		props.customCaseDetailsData?.reduce((acc: any, c: any, i) => {
			const currentWidth = getLengthShortAttributes(c.attribute.expectedWidth);
			if (i === 0) {
				return acc;
			}
			if (i === 1) {
				return getLengthShortAttributes(acc.attribute.expectedWidth) + currentWidth;
			}
			if (acc + currentWidth <= totalRowsInShortAttributeCount) {
				return acc + currentWidth;
			}
			if (typeof acc === "number" && acc + currentWidth > totalRowsInShortAttributeCount) {
				return { slice: i };
			}
			return acc;
		});

	const customFields =
		typeof reducer === "object" && props.customCaseDetailsData && props.customCaseDetailsData?.length > 1
			? props.customCaseDetailsData?.slice(0, reducer.slice)
			: props.customCaseDetailsData;

	return (
		<Box display="flex" width="100%" flexDirection="column">
			<Box padding={1} display="flex" width="100%">
				<Box width="24%">
					<Typography noWrap variant="subtitle2" className={fw400} color="textSecondary">
						{startUpper(t("caseID"))}
					</Typography>
					<Typography>{props.caseId || EMPTY_VALUE_PLACEHOLDER}</Typography>
				</Box>
				<Box width="24%">
					<Typography noWrap variant="subtitle2" className={fw400} color="textSecondary">
						{startUpper(t("agreementID"))}
					</Typography>
					{props.agreementId && (
						<Box display="flex" alignItems="center">
							<Typography>{props.agreementId || EMPTY_VALUE_PLACEHOLDER}</Typography>
							&nbsp;
							<Box height="18px">
								<Link
									style={{ cursor: "pointer" }}
									onClick={(e: React.SyntheticEvent) => handleNavigateToAgreement(e, props.agreementId || "")}
								>
									<OpenInNew className={icon} color="primary" />
								</Link>
							</Box>
						</Box>
					)}
				</Box>
				<Box width="24%">
					<Typography noWrap variant="subtitle2" className={fw400} color="textSecondary">
						{startUpper(t("creation-date"))}
					</Typography>
					<Typography>{localeDate(props.creationDate)}</Typography>
				</Box>
				{props.legalEntity && (
					<Box width="20%">
						<Typography noWrap variant="subtitle2" className={fw400} color="textSecondary">
							{startUpper(t("legal-entity"))}
						</Typography>
						<Typography>{props.legalEntity}</Typography>
					</Box>
				)}
			</Box>
			<Box paddingY={1} display="flex" width="20%" flexGrow={1}>
				<CaseAssignee caseId={props.caseId} assignedTo={props.assignedTo} />
			</Box>
			{props.relevantIndication && (
				<Box padding={1} display="flex" width="40%" flexGrow={1}>
					<Box>
						<Typography noWrap variant="subtitle2" className={fw400} color="textSecondary">
							{startUpper(t("relevantIndication"))}
						</Typography>
						<Typography>{props.relevantIndication.name}</Typography>
					</Box>
				</Box>
			)}
			<Box display="flex" maxWidth="100%" flexWrap="wrap">
				{props.customCaseDetailsData &&
					customFields &&
					customFields.map((item, i) => (
						<Box
							padding={1}
							key={i}
							className={getColumnClassName(item.attribute.expectedWidth || AttributeSize.Medium, styles)}
						>
							<Typography noWrap variant="subtitle2" className={fw400} color="textSecondary">
								{t(item.attribute.shortLabelLocalizationKey)}
							</Typography>
							<Typography>{item.value}</Typography>
						</Box>
					))}
			</Box>
		</Box>
	);
};

export default CaseInformationDetails;
